import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Phone, MapPin, CheckCircle2, Sparkles, Hammer, PencilRuler, Star } from "lucide-react";
import { SITE, COMPLETED_PROJECTS } from "../data/site";
import { CITIES } from "../data/cities";
import { usePageMeta } from "../hooks/usePageMeta";

const CITY_GALLERY = COMPLETED_PROJECTS.map((p) => ({ src: p.src, label: p.title }));

const DESIGN_TILES = [
  {
    title: "Finishes",
    desc: "Marble-look panel, acrylic, PU stone veneer, painted, wood grain.",
  },
  {
    title: "Layouts",
    desc: "TV wall, TV + fireplace, full media wall, bookcase build, accent wall.",
  },
  {
    title: "Cabinetry",
    desc: "Floating console, floor cabinets, one or two towers, bookcase cubbies.",
  },
  {
    title: "Lighting",
    desc: "Behind-TV, shelf/cubby, under-console, accent. Warm white or color-changing.",
  },
];

const PROCESS_STEPS = [
  { icon: PencilRuler, title: "Design selections", desc: "Use the wall builder or share your inspiration and we'll draft options." },
  { icon: MapPin, title: "Consultation & measurements", desc: "We visit the space, take exact measurements and confirm feasibility." },
  { icon: Star, title: "Proposal", desc: "A written scope with materials, timeline and pricing — no surprise upcharges." },
  { icon: Hammer, title: "Scheduling & installation", desc: "Fabrication in our shop, install on-site with your home protected." },
];

export default function CityPage() {
  const { city } = useParams();
  const data = CITIES[city];

  usePageMeta({
    title: data?.seo_title,
    description: data?.meta_description,
    canonicalPath: `/locations/${city}`,
  });

  if (!data) return <Navigate to="/locations" replace />;

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-16 grain">
      <div className="container-x px-5 md:px-10 lg:px-16">
        {/* Breadcrumb */}
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#A1A1AA] mb-6">
          <Link to="/" className="hover:text-[#D4AF37]">
            Home
          </Link>{" "}
          / <Link to="/locations" className="hover:text-[#D4AF37]">
            Service Areas
          </Link>{" "}
          / <span className="text-[#D4AF37]">{data.name}</span>
        </div>

        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">
              <MapPin className="w-3.5 h-3.5" /> {data.name}, {data.state}
            </div>
            <h1 data-testid="city-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight mt-4">
              {data.hero_heading}
            </h1>
            <p className="text-sm md:text-base text-[#D1D1D6] mt-5 leading-relaxed max-w-2xl">
              {data.hero_sub}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/design-my-wall"
                data-testid={`city-cta-design-${data.slug}`}
                className="btn-gold !py-3 !px-6"
              >
                <Sparkles className="w-4 h-4" /> Design My Wall <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={`tel:${SITE.phoneDigits}`}
                data-testid={`city-cta-call-${data.slug}`}
                className="btn-ghost !py-3 !px-6"
              >
                <Phone className="w-4 h-4" /> Request Consultation
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-[#27272A] overflow-hidden">
              <img
                src={COMPLETED_PROJECTS[0].src}
                alt={`${data.name} media wall reference`}
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Services intro */}
        <section className="mt-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
              What we build in {data.name}
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 leading-tight">
              Custom-built for {data.name} homes
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-sm md:text-base text-[#D1D1D6] leading-relaxed">
            <p>{data.focus}</p>
            <p>{data.services_intro}</p>
          </div>
        </section>

        {/* Neighborhoods */}
        <section className="mt-16">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
            Neighborhoods we serve
          </div>
          <div className="flex flex-wrap gap-2.5">
            {data.neighborhoods.map((n) => (
              <span
                key={n}
                data-testid={`city-neighborhood-${n.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#27272A] text-sm text-[#D1D1D6]"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                {n}
              </span>
            ))}
          </div>
        </section>

        {/* Reference gallery */}
        <section className="mt-20">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
                Project references
              </div>
              <h2 className="font-display text-3xl md:text-4xl mt-2">Inspiration for your {data.name} build</h2>
            </div>
            <Link to="/#gallery" className="link-gold text-sm text-[#D1D1D6]">
              See full gallery →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CITY_GALLERY.map((g, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-[#27272A] group">
                <div className="aspect-[4/5] overflow-hidden bg-[#111]">
                  <img
                    src={g.src}
                    alt={g.label}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="px-4 py-3 text-xs tracking-wide text-[#D1D1D6]">{g.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Design choices */}
        <section className="mt-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
            Design choices & pricing
          </div>
          <h2 className="font-display text-3xl md:text-4xl">Choose the finishes, layout and features that fit your space</h2>
          <p className="text-sm text-[#A1A1AA] mt-3 max-w-3xl leading-relaxed">
            Size, materials, cabinetry and lighting all influence cost. Most {data.name} single-wall projects fall
            between roughly $3,500 and $12,000+ depending on scope — the wall builder gives you a working range
            after a few quick selections.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {DESIGN_TILES.map((t) => (
              <div
                key={t.title}
                className="rounded-2xl border border-[#27272A] bg-[#0d0d0d] p-6"
              >
                <div className="font-display text-xl text-white">{t.title}</div>
                <div className="text-sm text-[#A1A1AA] mt-2 leading-relaxed">{t.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Installation process */}
        <section className="mt-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">Installation process</div>
          <h2 className="font-display text-3xl md:text-4xl">Design selections → install day, cleanly</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {PROCESS_STEPS.map((s, i) => (
              <div key={i} className="rounded-2xl border border-[#27272A] bg-[#0d0d0d] p-6 relative">
                <div className="absolute top-4 right-4 text-[10px] tracking-[0.24em] uppercase text-[#6b6b72]">
                  0{i + 1}
                </div>
                <s.icon className="w-6 h-6 text-[#D4AF37]" />
                <div className="font-display text-lg text-white mt-4">{s.title}</div>
                <div className="text-sm text-[#A1A1AA] mt-2 leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Local details */}
        <section className="mt-20 rounded-2xl border border-[#27272A] bg-[#0a0a0a] p-8 md:p-10">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
            Working in {data.name}
          </div>
          <p className="text-sm md:text-base text-[#D1D1D6] leading-relaxed max-w-3xl">{data.local_details}</p>
        </section>

        {/* FAQ */}
        <section className="mt-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">Common questions</div>
          <h2 className="font-display text-3xl md:text-4xl mb-8">{data.name} FAQs</h2>
          <div className="space-y-3 max-w-3xl">
            {data.faqs.map((f, i) => (
              <details
                key={i}
                data-testid={`faq-${data.slug}-${i}`}
                className="group rounded-xl border border-[#27272A] bg-[#0d0d0d] open:border-[#AA8035] transition-colors"
              >
                <summary className="cursor-pointer list-none px-6 py-5 flex items-start justify-between gap-6">
                  <div className="font-display text-lg text-white">{f.q}</div>
                  <span className="flex-none mt-1 text-[#D4AF37] text-xl group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <div className="px-6 pb-5 text-sm text-[#D1D1D6] leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20 rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0a0a0a] via-[#111] to-[#050505] p-10 md:p-14 text-center relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.14) 0%, transparent 60%)" }}
          />
          <CheckCircle2 className="w-8 h-8 text-[#D4AF37] mx-auto" />
          <h2 className="font-display text-3xl md:text-5xl mt-4">
            Design your {data.name} wall in minutes
          </h2>
          <p className="text-sm md:text-base text-[#A1A1AA] mt-4 max-w-xl mx-auto">
            Answer a few visual questions and Rob will review your selections personally — no drawn-out sales
            back-and-forth.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/design-my-wall"
              data-testid={`city-cta-design-bottom-${data.slug}`}
              className="btn-gold !py-3 !px-6"
            >
              <Sparkles className="w-4 h-4" /> Design My Wall
            </Link>
            <a href={`tel:${SITE.phoneDigits}`} className="btn-ghost !py-3 !px-6">
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
